import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';

interface TemplateColumnMapperProps {
  template_id: string;
  whatsapp_template_name?: string;
  google_sheet_id: string;
  google_sheet_tab_id?: number | null;
  google_sheet_tab_name?: string;
  phone_column: string;
  onMappingComplete: (mapping: Record<string, string>) => void;
  onBack: () => void;
  initialMapping?: Record<string, string>;
}

export const TemplateColumnMapper: React.FC<TemplateColumnMapperProps> = ({
  template_id,
  whatsapp_template_name,
  google_sheet_id,
  google_sheet_tab_id,
  google_sheet_tab_name,
  phone_column,
  onMappingComplete,
  onBack,
  initialMapping = {}
}) => {
  const [variables, setVariables] = useState<string[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>(initialMapping);
  const [suggestedMapping, setSuggestedMapping] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    console.log('🔄 TemplateColumnMapper - useEffect triggered', {
      template_id,
      whatsapp_template_name,
      google_sheet_id,
      google_sheet_tab_name
    });
    loadTemplateVariables();
    }, [template_id, whatsapp_template_name, google_sheet_id, google_sheet_tab_id, google_sheet_tab_name]);

  const loadTemplateVariables = async () => {
    setLoading(true);
    try {
      const payload: any = {
        google_sheet_id,
        phone_column
      };

      if (google_sheet_tab_id != null) {
        payload.google_sheet_tab_id = google_sheet_tab_id;
      }

      if (google_sheet_tab_name) {
        payload.google_sheet_tab_name = google_sheet_tab_name;
      }

      // Send template_id or whatsapp_template_name (at least one is required)
      if (template_id) {
        payload.template_id = template_id;
      }
      if (whatsapp_template_name) {
        payload.whatsapp_template_name = whatsapp_template_name;
      }

      console.log('🔍 TemplateColumnMapper - Loading variables with payload:', payload);

      // Validar que al menos uno de los dos esté presente
      if (!template_id && !whatsapp_template_name) {
        console.error('❌ No template_id or whatsapp_template_name provided');
        toast.error('No se ha seleccionado ninguna plantilla. Por favor regresa y selecciona una plantilla.');
        setLoading(false);
        return;
      }

      const response = await api.post('/campaigns/template-variables', payload);

      console.log('✅ Template variables response:', response.data);

      const { variables: vars, columns: cols, suggested_mapping } = response.data;

      setVariables(vars);
      setColumns(cols);
      setSuggestedMapping(suggested_mapping || {});

      // Initialize mapping with suggested values or initial mapping
      const initialMap = { ...mapping };
      vars.forEach((variable: string) => {
        if (!initialMap[variable]) {
          initialMap[variable] = suggested_mapping[variable] || '';
        }
      });
      setMapping(initialMap);

      if (Object.keys(suggested_mapping || {}).length > 0) {
        toast.success(`Se detectaron ${Object.keys(suggested_mapping).length} coincidencias automáticas`);
      }
    } catch (error: any) {
      console.error('❌ Error loading template variables:', error);
      console.error('Response data:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Error al cargar las variables del template');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (variable: string, column: string) => {
    setMapping(prev => ({
      ...prev,
      [variable]: column
    }));
  };

  const handleContinue = () => {
    // Validate all variables are mapped
    const unmapped = variables.filter(v => !mapping[v] || mapping[v] === '');
    
    if (unmapped.length > 0) {
      toast.error(`Por favor mapea las siguientes variables: ${unmapped.join(', ')}`);
      return;
    }

    onMappingComplete(mapping);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Analizando template y columnas...</p>
        </CardContent>
      </Card>
    );
  }

  if (variables.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>✅</span>
            <span>Configuración Completa</span>
          </CardTitle>
          <CardDescription>
            Este template no tiene variables dinámicas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            El template seleccionado no contiene variables que requieran mapeo.
          </p>
          <div className="flex justify-between">
            <Button variant="outline" onClick={onBack}>
              ← Atrás
            </Button>
            <Button 
              onClick={() => onMappingComplete({})}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
            >
              Continuar →
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <span>🔗</span>
          <span>Mapear Variables del Template</span>
        </CardTitle>
        <CardDescription>
          Relaciona cada variable del template con una columna de tu hoja
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2">
            <span className="text-blue-600 text-xl">ℹ️</span>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">¿Qué es esto?</p>
              <p>
                Tu template contiene variables (como <code className="bg-white px-1 rounded">{'{{Nombre}}'}</code>).
                Necesitas indicar de qué columna de tu hoja se tomará cada valor.
              </p>
            </div>
          </div>
        </div>

        {variables.map((variable) => {
          const isAutoMatched = suggestedMapping[variable] === mapping[variable];
          
          return (
            <div key={variable} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-medium">
                  Variable: <code className="bg-white px-2 py-1 rounded text-blue-600">{'{{' + variable + '}}'}</code>
                </Label>
                {isAutoMatched && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    ✓ Auto-detectado
                  </span>
                )}
              </div>
              
              <select
                value={mapping[variable] || ''}
                onChange={(e) => handleMappingChange(variable, e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Selecciona una columna --</option>
                {columns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
              
              {mapping[variable] && (
                <p className="text-xs text-gray-600 mt-2">
                  Los valores de la columna <strong>{mapping[variable]}</strong> se usarán para {'{{' + variable + '}}'}.
                </p>
              )}
            </div>
          );
        })}

        <div className="border-t pt-4 mt-6">
          <h4 className="font-medium text-sm text-gray-700 mb-2">Resumen del mapeo:</h4>
          <ul className="text-sm space-y-1">
            {variables.map((variable) => (
              <li key={variable} className="flex items-center justify-between">
                <span className="text-gray-600">
                  <code className="bg-gray-100 px-1 rounded">{'{{' + variable + '}}'}</code>
                </span>
                <span className="text-gray-900">
                  {mapping[variable] ? (
                    <span className="font-medium">→ {mapping[variable]}</span>
                  ) : (
                    <span className="text-red-500">⚠️ Sin mapear</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>
            ← Atrás
          </Button>
          <Button 
            onClick={handleContinue}
            disabled={variables.some(v => !mapping[v])}
            className="bg-gradient-to-r from-blue-600 to-purple-600"
          >
            Continuar a Vista Previa →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TemplateColumnMapper;
